import React, { FC } from 'react';
import clsx from 'clsx';
import { AImage, AImageAspectRatio, AImageMediaQuery, generateImageRenditions } from '../../atoms/AImage/index';
import { MBrandElement } from '../../molecules/MBrandElement/index';
import { MTextBlockFull } from '../../molecules/MTextBlockFull/index';
import { MButton } from '../../molecules/MButton/index';
import { MPhotoCaption } from '../../molecules/MPhotoCaption/index';
import type { ImageRendition } from '../../utils/ImageUtils';
import * as Styled from './OHeroContentBlock.styles';
import {
  OHeroContentBlockProps,
  OHeroContentBlockDesktopContentAlignment,
  OHeroContentBlockImageScale,
} from './OHeroContentBlock.types';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OHeroContentBlock';
const TEST_ID = 'mi-web-ui-kit-OHeroContentBlock';

export const OHeroContentBlock: FC<OHeroContentBlockProps> = ({
  buttonGroup = false,
  photoCaption = false,
  desktopContentAlignment = OHeroContentBlockDesktopContentAlignment.CENTER,
  imageScale = OHeroContentBlockImageScale.MEDIUM,
  aImage,
  mBrandElement,
  mTextBlockFull,
  mButton,
  mButton2,
  mPhotoCaption,
  className,
  ...rest
}) => {
  const generateHeroImageRenditions = (): ImageRendition[] | undefined => {
    if (!aImage?.image?.assetPath) return undefined;

    const scaleConfig = {
      [OHeroContentBlockImageScale.SMALL]: [
        { media: AImageMediaQuery.MIN_WIDTH_XL, aspectRatio: AImageAspectRatio.WIDE_HOR, imgWidthPx: 800 },
        { media: AImageMediaQuery.MIN_WIDTH_MD, aspectRatio: AImageAspectRatio.CLASSIC_HOR, imgWidthPx: 600 },
        { media: AImageMediaQuery.MIN_WIDTH_XS, aspectRatio: AImageAspectRatio.SQUARE, imgWidthPx: 400 },
      ],
      [OHeroContentBlockImageScale.MEDIUM]: [
        { media: AImageMediaQuery.MIN_WIDTH_XL, aspectRatio: AImageAspectRatio.WIDE_HOR, imgWidthPx: 1200 },
        { media: AImageMediaQuery.MIN_WIDTH_MD, aspectRatio: AImageAspectRatio.CLASSIC_HOR, imgWidthPx: 900 },
        { media: AImageMediaQuery.MIN_WIDTH_XS, aspectRatio: AImageAspectRatio.SQUARE, imgWidthPx: 600 },
      ],
      [OHeroContentBlockImageScale.LARGE]: [
        { media: AImageMediaQuery.MIN_WIDTH_XL, aspectRatio: AImageAspectRatio.WIDE_HOR, imgWidthPx: 1600 },
        { media: AImageMediaQuery.MIN_WIDTH_MD, aspectRatio: AImageAspectRatio.CLASSIC_HOR, imgWidthPx: 1200 },
        { media: AImageMediaQuery.MIN_WIDTH_XS, aspectRatio: AImageAspectRatio.SQUARE, imgWidthPx: 800 },
      ],
    };

    return generateImageRenditions(scaleConfig[imageScale], aImage.image.assetPath);
  };

  const imageRenditions = generateHeroImageRenditions();

  return (
    <Styled.HeroContentBlockWrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx(className)}
      $desktopContentAlignment={desktopContentAlignment}
      $imageScale={imageScale}
      {...rest}
    >
      <Styled.ImageContainer $imageScale={imageScale}>
        {aImage?.image && (
          <AImage
            image={aImage.image}
            renditions={imageRenditions}
            loading="eager"
            title={aImage.image.altText}
            imgClass="hero-image"
          />
        )}
      </Styled.ImageContainer>

      <Styled.ContentContainer $desktopContentAlignment={desktopContentAlignment}>
        <Styled.ContentInner>
          {mBrandElement && (
            <Styled.BrandElementWrapper>
              <MBrandElement {...mBrandElement} />
            </Styled.BrandElementWrapper>
          )}

          {mTextBlockFull && (
            <Styled.TextBlockWrapper>
              <MTextBlockFull {...mTextBlockFull} />
            </Styled.TextBlockWrapper>
          )}

          {buttonGroup && (mButton || mButton2) && (
            <Styled.ButtonGroupWrapper $hasSecondaryButton={!!mButton2}>
              {mButton && (
                <Styled.ButtonWrapper>
                  <MButton {...mButton} />
                </Styled.ButtonWrapper>
              )}
              {mButton2 && (
                <Styled.ButtonWrapper>
                  <MButton {...mButton2} />
                </Styled.ButtonWrapper>
              )}
            </Styled.ButtonGroupWrapper>
          )}

          {photoCaption && mPhotoCaption && (
            <Styled.PhotoCaptionWrapper>
              <MPhotoCaption {...mPhotoCaption} />
            </Styled.PhotoCaptionWrapper>
          )}
        </Styled.ContentInner>
      </Styled.ContentContainer>
    </Styled.HeroContentBlockWrapper>
  );
};

export * from './OHeroContentBlock.types';
